<script setup>
import { storeToRefs } from 'pinia'
import { Html5Qrcode } from 'html5-qrcode'
import NoWebcam from 'assets/icons/no-webcam.svg'
import { useMessageStore } from '~/stores/messages'

const props = defineProps({
    scannerConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                isVisible: value => value?.isVisible === undefined || typeof value?.isVisible === 'boolean',
                title: value => value?.title === undefined || typeof value?.title === 'string'
            }

            return Object.entries(validations).every(([_, validation]) => validation(rawValue))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Config for submit button
const submitButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.parking_ticketpayment_step1_barcode_cta,
        buttonConfig: {
            type: 'div',
            buttonType: 'secondary',
            large: true,
            active: true
        }
    }
})

// Define emits
const emit = defineEmits(['update:toggleBarcodeScannerVisibility', 'update:emitBarcodeScannerResult'])

// Init barcode scanner
const barCodeConfig = {
    fps: 10,
    qrbox: {
        width: 300,
        height: 150
    },
    // aspectRatio: 0.7777777,
    useBarCodeDetectorIfSupported: true,
    showTorchButtonIfSupported: true,
    defaultZoomValueIfSupported: 2,
    showZoomSliderIfSupported: !(/iPad|iPhone|iPod/.test(navigator.platform)) // Bug...https://github.com/mebjas/html5-qrcode/issues/912
}

const dataScrollTriggerTop = ref(null)
const fileUploadInProgress = ref(false)
const isScanning = ref(false)
const isUploading = ref(false)
const fileRef = ref(null)
const cameraList = ref([])
const activeCamera = ref()
const error = ref(false)
const errorMessage = ref('')
const success = ref(false)
const successMessage = ref('')
const noCamerasFound = ref(false)

let timeout = false
let html5QrCode

// Set loading state
const isLoading = computed(() => !isScanning.value && !isUploading.value && !error.value && !fileUploadInProgress.value)

/**
 * Resets the error state by setting error value to false and clearing the error message.
 *
 * @return {void} This function does not return a value.
 */
const resetError = () => {
    error.value = false
    errorMessage.value = ''
}

/**
 * Sets the error state and displays an error message.
 *
 * @param {string} message - The error message to display.
 * @param {string} type - The type of error message to display. Can be 'upload' or any other value.
 * @return {void} This function does not return a value.
 */
const showErrorMessage = (message, type) => {
    console.log(message)

    error.value = true
    errorMessage.value = type === 'upload' ? staticKeys.value?.parking_ticketpayment_step1_barcode_upload_error_web : staticKeys.value?.parking_ticketpayment_step1_barcode_scan_error_web

    scrollToTop()
}

/**
 * Sets the success state and displays a success message.
 *
 * @param {string} message - The message to display as a success.
 * @return {void} This function does not return a value.
 */
const showSuccessMessage = (message) => {
    console.log(message)

    success.value = true
    successMessage.value = staticKeys.value?.parking_ticketpayment_step1_barcode_success_web

    scrollToTop()
}

const scrollToTop = () => {
    // Scroll to target
    dataScrollTriggerTop.value?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
    })
}
/**
 * Retrieves the list of available cameras and initializes the QR code scanner.
 *
 * @return {Promise<void>} A promise that resolves when the cameras are retrieved and the scanner is initialized.
 */
const getCameras = () => {
    Html5Qrcode.getCameras()
        .then((devices) => {
            if (devices && devices.length) {
                cameraList.value = devices
                activeCamera.value = devices[0]

                handleScanInit()
            } else {
                noCamerasFound.value = true
            }
        })
        .catch((err) => {
            console.error(err)
            cameraList.value = []
            noCamerasFound.value = true
        })
}

/**
 * Initializes the QR code scanner and starts scanning for QR codes.
 *
 * @return {Promise<void>} A promise that resolves when the scanning is started.
 */
const handleScanInit = () => {
    const qrCodeSuccessCallback = (decodedText, _) => {
        showSuccessMessage(decodedText)
        handleBarCodeSuccess(decodedText)
    }

    isScanning.value = false
    isUploading.value = false
    fileUploadInProgress.value = false

    resetError()

    html5QrCode.start(
        { facingMode: 'environment' },
        barCodeConfig,
        qrCodeSuccessCallback
    ).then(() => {
        isScanning.value = true
    })
}

/**
 * Stops the QR code scanner and clears the scanner. If `initScanner` is true,
 * it also retrieves the available cameras. If `initFileUpload` is true, it
 * triggers the file input.
 *
 * @return {Promise<void>} A promise that resolves when the scanner is stopped
 * and cleared
 */
const handleScanStop = () => {
    if (html5QrCode?.isScanning) {
        try {
            html5QrCode.stop().then((_) => {
                html5QrCode.clear()

                isScanning.value = false
            }).catch((err) => {
                console.log(err)
                showErrorMessage(err, 'scan')
            })
        } catch (err) {
            console.log(err)
            showErrorMessage(err, 'scan')
        }
    }
}

/**
 * Triggers a local file upload by stopping scanning and clicking on the file input element.
 *
 * @return {void} This function does not return a value.
 */
const triggerLocalFileUpload = () => {
    // Trigger file upload
    fileRef.value?.click()
}

/**
 * Handles the start of a file upload event.
 *
 * @param {Event} e - The file upload event.
 * @return {void} This function does not return a value.
 */
const handleFileUploadStart = async (e) => {
    const files = e.target?.files

    if (files?.length > 0) {
        // Reset the error state
        resetError()
        noCamerasFound.value = false

        // First stop scanning
        if (html5QrCode.isScanning) {
            await handleScanStop()
        }

        html5QrCode.scanFile(files[0], true)
            .then((qrCodeMessage) => {
                // Set the state
                isUploading.value = true

                showSuccessMessage(qrCodeMessage)

                // Emit the result
                handleBarCodeSuccess(qrCodeMessage, true)
            }).catch((err) => {
                console.log(err)

                showErrorMessage(err, 'upload')
            }).finally(() => {
                fileUploadInProgress.value = true
            })
    }
}

/**
 * Handles the success of a barcode scan.
 *
 * @param {string} message - The message from the barcode scan.
 * @param {boolean} [delay=false] - Whether to delay the closing of the scanner and layer.
 * @return {void} This function does not return a value.
 */
const handleBarCodeSuccess = (message, delay = false) => {
    // Emit the result
    emit('update:emitBarcodeScannerResult', message)

    // Handle delay (file upload)
    if (delay) {
        // Clear the scanner and close layer after 3 seconds
        setTimeout(() => {
            emit('update:toggleBarcodeScannerVisibility', false)
        }, 3000)
    } else {
        emit('update:toggleBarcodeScannerVisibility', false)
    }
}

/**
 * Handles the window resize event by stopping the QR code scanning process if it is currently active.
 *
 * @return {void} This function does not return a value.
 */
const handleWindowResize = () => {
    // Handle resize of scan video
    if (html5QrCode.isScanning) {
        handleScanStop()
        getCameras()
    }
}

onMounted(() => {
    // Init Html5Qrcode
    html5QrCode = new Html5Qrcode('qr-reader')

    // Get cameras and init scanner
    getCameras()

    // Initialize resize listener
    window.addEventListener('resize', () => {
        clearTimeout(timeout)
        timeout = setTimeout(handleWindowResize, 500)
    })
})

onUnmounted(() => {
    // Remove resize listener
    window.removeEventListener('resize', handleWindowResize)

    // Stop scanning
    handleScanStop()
})
</script>

<template>
    <dialog :open="props.scannerConfig.isVisible" class="barcode-scanner-layer">
        <section ref="dataScrollTriggerTop" class="barcode-scanner-wrapper">
            <header class="barcode-scanner-header">
                <ContentMainHeadline
                    :content-main-headline-config="{
                        title: `_${scannerConfig.title}_`,
                        align: 'center'
                    }"
                />

                <FormMessage
                    :form-message-config="{
                        error: error,
                        success: success,
                        message: errorMessage || successMessage
                    }"
                    @update:set-status="resetError"
                />
            </header>

            <div class="barcode-scanner-container">
                <div v-if="noCamerasFound" class="no-cameras-found-hint">
                    <NoWebcam />

                    <p>
                        {{ staticKeys.parking_ticketpayment_step1_barcode_no_camera_intro1_web }}
                        <br>
                        <br>
                        {{ staticKeys.parking_ticketpayment_step1_barcode_no_camera_intro2_web }}
                        <span class="barcode-cta-link" @click="triggerLocalFileUpload">
                            {{ staticKeys.parking_ticketpayment_step1_barcode_no_camera_upload_link_label_web }}
                        </span>

                        {{ staticKeys.parking_ticketpayment_step1_barcode_no_camera_intro3_web }}
                        <span class="barcode-cta-link" @click="emit('update:toggleBarcodeScannerVisibility', false)">
                            {{ staticKeys.parking_ticketpayment_step1_barcode_no_camera_manual_link_label_web }}
                        </span>.
                    </p>
                </div>
                <template v-else>
                    <div class="qr-reader-container">
                        <div id="qr-reader" class="qr-reader" />
                        <LoadingSpinner v-if="isLoading" class="container-loader" />
                    </div>

                    <p v-if="isScanning" class="barcode-scanner-hint">
                        {{ staticKeys.parking_ticketpayment_step1_barcode_hint }}
                    </p>

                    <div class="barcode-action-bar">
                        <template v-if="fileUploadInProgress">
                            <div class="barcode-cta-link" @click="triggerLocalFileUpload">
                                {{ staticKeys.parking_ticketpayment_step1_barcode_reupload_cta_web }}
                            </div>
                            <p>{{ staticKeys.parking_ticketpayment_step1_barcode_or_web }}</p>
                            <div class="barcode-cta-link" @click="handleScanInit">
                                {{ staticKeys.parking_ticketpayment_step1_barcode_scan_cta_web }}
                            </div>
                        </template>
                        <template v-else>
                            <p>{{ staticKeys.parking_ticketpayment_step1_barcode_or_web }}</p>
                            <div class="barcode-cta-link" @click="triggerLocalFileUpload">
                                {{ staticKeys.parking_ticketpayment_step1_barcode_upload_cta_web }}
                            </div>
                        </template>
                    </div>
                </template>

                <input
                    ref="fileRef"
                    type="file"
                    hidden
                    accept="image/*"
                    @change="handleFileUploadStart"
                >
            </div>

            <div class="barcode-cancel-cta">
                <FormButton
                    :form-button-config="submitButtonConfig"
                    @click="emit('update:toggleBarcodeScannerVisibility', false)"
                />
            </div>
        </section>
    </dialog>
</template>

<style lang="scss">
.barcode-scanner-layer {
    border: 0 none;
    align-items: flex-start;
    display: flex;
    flex-flow: row wrap;
    position: fixed;
    top: 0;
    left: 0;
    z-index: z('barcode-scanner-layer');
    background: $barcode-scanner-layer-bg;
    width: 100vw;
    height: 100dvh;
    padding: 0 0 150px;

    @include hide-scrollbar;

    @include bp(large1) {
        padding-bottom: 40px;
    }
}

.barcode-scanner-wrapper {
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
    align-items: flex-start;
    display: flex;
    flex-flow: row wrap;
    min-height: 100%;
}

.barcode-scanner-header {
    text-align: center;
    align-self: flex-start;
    width: 100%;
    margin-block: px2rem(40) px2rem(10);
    padding-inline: px2rem(20);

    @include bp(large1) {
        margin-block: px2rem(40) px2rem(20);
    }
}

.barcode-scanner-container {
    width: 100%;
    border: 0 none !important;
    flex: 1;
    font-family: $font-body;
    color: $body-text;
    text-align: center;
    position: relative;

    @include body2-medium;
}

.no-cameras-found-hint {
    padding-inline: px2rem(20);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column wrap;
    max-width: 350px;
    margin: 0 auto;

    @include body2-medium;

    svg {
        width: 70px;
    }
}

.qr-reader-container {
    position: relative;
}

.qr-reader {
    aspect-ratio: 0.777;
    overflow: hidden;
    position: relative;
    background: $qr-reader-bg;

    video,
    canvas {
        position: absolute;
        inset: 0;
        height: 100% !important;
        width: 100% !important;
    }
}

.barcode-scanner-hint {
    color: $barcode-scanner-hint;
    text-align: center;
    z-index: z('barcode-scanner-hint');
    margin-block: -40px 40px;
    position: relative;

    @include body2-medium;
}

.barcode-cta-link {
    @include textlink;
}

.barcode-action-bar {
    margin-block-start: px2rem(30);
}

.barcode-cancel-cta {
    width: 100%;
    text-align: center;
    margin-block-start: auto;
    align-self: flex-end;
    padding: 40px 20px 0;
}
</style>
